import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnInit,
    ViewChild
} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {FormBuilder, FormGroup, ValidationErrors} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';

import {
    faChevronUp,
    faChevronDown,
    faArrowUp
} from '@fortawesome/free-solid-svg-icons';
import Swal, {SweetAlertIcon} from 'sweetalert2';
import {ScrollToService} from '@nicky-lenaers/ngx-scroll-to';

import {environment} from 'environments/environment';
import {CameraSettingsData} from '@/data/camera-settings.data';

import {SelectionDialogComponent} from './selection-dialog/selection-dialog.component';
import {ChangesDialogComponent} from './changes-dialog/changes-dialog.component';

import {MatrixMiniProps} from './MatrixMiniProps';
import {Matrix2PlusProps} from './Matrix2PlusProps';
import {Matrix4GProps} from './Matrix4GProps';

import {
    Camera,
    CameraSettingGroup,
    CameraSettingTab,
    CameraType,
    CameraTypes,
    ExternalCameraSetting,
    GenericCameraSettings,
    GenericCameraSettingsFunctions,
    Group,
    InputType,
    CameraSettingsErrorStateMatcher,
    RenderType,
    TabWithIcon,
    ApiMode,
    cameraSettingGroups,
    tabs,
    cameraList,
    Organization,
    Role
} from './models';

import {CameraSettingService} from '@services/camerasettings.service';
import {OrganizationService} from '@services/organization.service';
import {RoleService} from '@services/role.service';

@Component({
    selector: 'app-camera-setting',
    templateUrl: './camera-setting.component.html',
    styleUrls: ['./camera-setting.component.scss']
})
export class CameraSettingComponent implements OnInit, AfterViewInit {
    @ViewChild('scrollableDiv') scrollableDiv: ElementRef;
    @ViewChild('scrollToTopButton') scrollToTopButton: ElementRef;

    isSiteAdmin: boolean;
    isProduction: boolean;

    selectedCamera: Camera = cameraList[0];
    selectedCameraId: number = cameraList[0].id;
    selectedCameraIdOld: number = 0;

    imagePrefix: string;
    selectedCameraImage: string;

    tabs: TabWithIcon[] = tabs;
    groups: Group[] = cameraSettingGroups;

    selectedTabId: number = 0;
    selectedTabBackup: number = 0;
    searchText: string = '';

    cameraList: Camera[] = cameraList;
    form: FormGroup = null;
    allowPageRender: Boolean = false;
    OrganizationName: any;
    RoleName: any;
    Organizations: Organization[];
    Roles: Role[];

    matcher = new CameraSettingsErrorStateMatcher();
    FormProperties: GenericCameraSettings[] = []; // Can be unreliable as it is being used to show/hide the properties based on the filter. Whenever FormProperty change we update the initial values
    InitialValues: GenericCameraSettings[] = []; // This contains the actual values and is being used to submit the form

    CameraSettings: CameraSettingTab[];
    panelOpenState: boolean = false;

    mode: ApiMode = ApiMode.User;
    faChevronUp = faChevronUp;
    faChevronDown = faChevronDown;
    faArrowUp = faArrowUp;

    queryCameraId = 5;
    queryUserId = 0;
    queryUsername = '';
    queryRoleId = 0;
    queryRoleName = '';
    queryCompanyId = 0;
    queryCompanyName = '';

    validationError: string =
        '<p>Please fix the validation errors before submitting.</p>';

    constructor(
        public dialog: MatDialog,
        public fb: FormBuilder,
        private route: ActivatedRoute,
        public cameraSettingService: CameraSettingService,
        public ChangeDetecter: ChangeDetectorRef,
        private scrollToService: ScrollToService,
        private organizationsService: OrganizationService,
        private roleService: RoleService
    ) {
        const check = window['isSiteAdmin'] ?? 'false';
        this.isSiteAdmin = check.toString().toLowerCase() === 'true';

        this.isProduction = environment.production;
        this.imagePrefix = this.isProduction
            ? 'Angular/WebForms/assets/img/'
            : 'assets/img/';

        this.ReadQueryString();
        this.SetupCameraSettings();

        if (!this.queryCameraId) {
            this.queryCameraId = 5; // * Default to Matrix Mini
        }
        this.selectedCameraImage = this.getCameraImage(this.queryCameraId); // * Default to Matrix Mini

        if (!this.queryUserId && !this.queryRoleId && !this.queryCompanyId) {
            const dialogRef = this.dialog.open(SelectionDialogComponent, {
                disableClose: true,
                data: {
                    properties: {},
                    userId: this.queryUserId,
                    cameraId: this.queryCameraId,
                    userName: this.queryUsername,
                    isSiteAdmin: this.isSiteAdmin
                }
            });

            dialogRef.afterClosed().subscribe((result) => {
                if (result && result.Status === true) {
                    this.queryRoleId = result.RoleId;
                    this.queryCompanyId = result.OrganizationId;
                    this.OrganizationName = result.OrganizationName;
                    this.RoleName = result.RoleName;
                    this.mode =
                        result.UpdateType === 'Organization'
                            ? ApiMode.Company
                            : ApiMode.Role;

                    this.GetCameraSettings();
                }
            });
        } else {
            this.GetCameraSettings();
        }
    }

    public get RenderType(): typeof RenderType {
        return RenderType;
    }

    public get InputType(): typeof InputType {
        return InputType;
    }

    get selectedTab(): CameraSettingTab[] {
        if (this.searchText !== '') {
            return this.CameraSettings;
        } else {
            return [
                this.CameraSettings.find(
                    (cs) => cs.tab.id == this.selectedTabId
                )!
            ];
        }
    }

    checkArrayEquality(a: any[], b: any[]) {
        if (a === b) return true;
        if (a == null || b == null) return false;
        if (a.length !== b.length) return false;

        for (var i = 0; i < a.length; ++i) {
            if (a[i] !== b[i]) return false;
        }
        return true;
    }

    onScroll(e: any) {
        if (this.scrollableDiv) {
            if (this.scrollableDiv.nativeElement.scrollTop > 200) {
                this.scrollToTopButton.nativeElement.style.display = 'block';
            } else {
                this.scrollToTopButton.nativeElement.style.display = 'none';
            }
        }
    }

    scrollToTop() {
        this.scrollableDiv.nativeElement.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }

    SetupCameraSettings() {
        this.CameraSettings = [];

        for (let i in this.tabs) {
            let groups = this.groups.filter((x) => x.tabId == this.tabs[i].id);
            let groupForCameraSetting: CameraSettingGroup[] = groups.map(
                (x: Group) => {
                    return {group: x, isOpen: true};
                }
            );

            groupForCameraSetting.forEach((x) => {
                if (this.selectedCameraId == 3)
                    x.initialValues = Matrix2PlusProps.filter(
                        (y) => y.groupId == x.group.id
                    ).sort((a, b) => {
                        // Custom sorting based on orderindex
                        if (a.orderindex < b.orderindex) return -1; // Ascending order
                        if (a.orderindex > b.orderindex) return 1;
                        return 0;
                    });

                if (this.selectedCameraId == 4)
                    x.initialValues = Matrix4GProps.filter(
                        (y) => y.groupId == x.group.id
                    ).sort((a, b) => {
                        // Custom sorting based on orderindex
                        if (a.orderindex < b.orderindex) return -1; // Ascending order
                        if (a.orderindex > b.orderindex) return 1;
                        return 0;
                    });

                if (this.selectedCameraId == 5)
                    x.initialValues = MatrixMiniProps.filter(
                        (y) => y.groupId == x.group.id
                    ).sort((a, b) => {
                        // Custom sorting based on orderindex
                        if (a.orderindex < b.orderindex) return -1; // Ascending order
                        if (a.orderindex > b.orderindex) return 1;
                        return 0;
                    });
            });

            this.CameraSettings.push({
                tab: this.tabs[i],
                groups: groupForCameraSetting
            });
        }
    }

    private ReadQueryString() {
        this.route.queryParams.subscribe((params) => {
            this.queryCameraId = parseInt(params.cameraId);

            if (!this.queryCameraId) {
                this.queryCameraId = 5;
            }
            this.selectedCameraImage = this.getCameraImage(this.queryCameraId);

            if (params.userId && params.cameraId) {
                this.queryUserId = params.userId;
                this.queryUsername = params.userName;
                this.mode = ApiMode.User;
            } else if (params.roleId && params.cameraId) {
                this.queryRoleId = params.roleId;
                this.mode = ApiMode.Role;
            } else if (params.companyId && params.cameraId) {
                this.queryCompanyId = params.companyId;
                this.mode = ApiMode.Company;
            }
        });
    }

    private FormInitialization() {
        this.FormProperties = this.InitialValues;

        this.FormProperties.reverse().forEach((x) => {
            this.ApplyEffects2(x.propertyName);
        });

        this.InitializeForm();
        this.allowPageRender = true;
    }

    private ApplyEffects2(parentPropertyName: string) {
        let prop = this.FormProperties.find(
            (x) => x.propertyName == parentPropertyName
        );
        let childPropsWithEffects = [];

        // * Update other properties based on changes in the current
        if (prop && prop.effects) {
            let effects2 = prop.effects.filter(
                (y) => y.givenValues.indexOf(prop.value) >= 0
            );

            effects2.forEach((effect) => {
                let effectedProps = this.FormProperties.filter(
                    (x) => effect.propertyNames.indexOf(x.propertyName) >= 0
                );

                if (effectedProps && effectedProps.length > 0) {
                    effectedProps.forEach((effectedProp) => {
                        let originalProp = this.InitialValues.find(
                            (z) => z.propertyName == effectedProp.propertyName
                        );

                        //  * Update disable state
                        if (
                            effect.disabled != null &&
                            effect.disabled != undefined
                        ) {
                            effectedProp.disabled = effect.disabled;
                            this.InitializeForm();
                        } else {
                            effectedProp.disabled = originalProp.disabled;
                        }

                        //  * Update value changes
                        if (
                            effect.value !== null &&
                            effect.value !== undefined
                        ) {
                            effectedProp.value = effect.value;
                        }

                        if (originalProp.effects && !originalProp.disabled) {
                            childPropsWithEffects.push(originalProp);
                        }
                    });
                }
            });

            const index = prop.effects.indexOf(
                prop.effects.find((effect) => effect.givenValues.length === 0)
            );

            if (index !== -1 && prop.effects[index].triggerChange) {
                prop.effects[index].propertyNames.forEach((pn) => {
                    const affectedProp = this.FormProperties.find(
                        (x) => x.propertyName == pn
                    );

                    if (affectedProp && affectedProp.changes) {
                        const propertiesToCheck =
                            affectedProp.changes.propertiesToCheck;
                        const propertyNameToChange =
                            affectedProp.changes.propertyNameToChange;
                        const values = affectedProp.changes.values;

                        let canChange = true;

                        if (propertiesToCheck.length == 0 || values.length == 0)
                            canChange = false;

                        values.forEach((val) => {
                            if (propertiesToCheck.length !== val.toCheck.length)
                                canChange = false;
                        });

                        if (canChange) {
                            // * Getting the properties that affecting the current property in the same order as "changes.propertiesToCheck" array
                            const affectingPropsValues = propertiesToCheck.map(
                                (pc) =>
                                    this.InitialValues.find((iv) =>
                                        iv.propertyName.includes(pc)
                                    ).value
                            );

                            const valueToSet = values.find((v) =>
                                this.checkArrayEquality(
                                    v.toCheck,
                                    affectingPropsValues
                                )
                            )?.toSet;

                            if (propertyNameToChange === 'maxSelectable') {
                                affectedProp.maxSelectable = valueToSet;

                                const preRecSecs = [
                                    0, 5, 10, 15, 20, 30, 60, 120
                                ];
                                const preRecAPIVals = [0, 1, 2, 3, 4, 5, 6, 7];

                                // * If current value exceeds the maximum value scenario
                                const val =
                                    preRecSecs[
                                        preRecAPIVals.indexOf(
                                            affectedProp.value as number
                                        )
                                    ];

                                if (val > valueToSet)
                                    affectedProp.value =
                                        preRecAPIVals[
                                            preRecSecs.indexOf(valueToSet)
                                        ];
                            }

                            if (propertyNameToChange === 'renderTypeValues')
                                affectedProp.renderTypeValues = valueToSet;

                            if (propertyNameToChange === 'disabled')
                                affectedProp.disabled = valueToSet;

                            this.InitializeForm();
                        }
                        // - Abort if no properties or their values are provided and wrong values set provided
                        else {
                            console.error(
                                'Wrong values set provided! No. of properties and their check values set should be equal',
                                prop.changes.propertiesToCheck,
                                prop.changes.values
                            );
                        }
                    }
                });
            }

            if (childPropsWithEffects.length > 0) {
                childPropsWithEffects.forEach((x) => {
                    this.ApplyEffects2(x.propertyName);
                });
            }
        }
    }

    private GetCameraSettings() {
        let k = 0;
        if (this.mode === ApiMode.Company) {
            k = this.queryCompanyId;
        } else if (this.mode === ApiMode.Role) {
            k = this.queryRoleId;
        } else if (this.mode === ApiMode.User) {
            k = this.queryUserId;
        }

        Swal.fire({
            html: `
                <h2 style="font-weight: 600;">Fetching camera settings!</h2>
                <p style="color: #777;">Please wait...</p>
            `,
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
        });

        this.cameraSettingService
            .getCameraSettings(k, this.mode, this.queryCameraId)
            .subscribe({
                next: (x: string) => {
                    this.SelectCamera(this.queryCameraId);

                    let a = JSON.parse(x)[0];
                    let result = {};

                    // * Only for dev
                    if (!this.isProduction && (a === undefined || null)) {
                        a = CameraSettingsData.MiniSettings;
                    }

                    Object.keys(a).map((e) => (result[e] = a[e]));

                    this.InitialValues.forEach((y) => {
                        let val = result[y.propertyName];

                        if (val != null && val != undefined) {
                            if (y.inputType == InputType.Number) {
                                y.value = Number(val || 0);
                                y.defaultValue = y.value || 0;
                            } else {
                                y.value = val;
                                y.defaultValue = y.value;
                            }
                        }
                    });

                    // * Updates for pre-record
                    let pre = true;
                    let post = true;

                    // * Condition for pre/post record
                    this.InitialValues.map((val) => {
                        if (
                            val.propertyName === 'PreRecordingTime' ||
                            val.propertyName === 'PostRecord'
                        ) {
                            // - Disable if it's 0
                            if (val.value === 0) {
                                val.disabled = true;

                                if (val.propertyName === 'PreRecordingTime')
                                    pre = false;

                                if (val.propertyName === 'PostRecord')
                                    post = false;
                            }
                        }
                    });

                    // * Setting boolean value to pre/post toggles based on their API values
                    this.InitialValues.map((val) => {
                        if (val.propertyName === 'PreRecordToggle') {
                            val.value = pre;
                            val.defaultValue = pre;
                        }
                        if (val.propertyName === 'PostRecordToggle') {
                            val.value = post;
                            val.defaultValue = post;
                        }
                    });

                    this.FormInitialization();
                    Swal.close();
                },
                error: (err) => {
                    Swal.close();
                    this.SweetAlert(
                        'error',
                        'Technical error',
                        'Unable to access the API resources'
                    );
                }
            });
    }

    getCameraImage(id: number) {
        switch (id) {
            case 3:
                return 'matrix_2plus.png';
            case 4:
                return 'matrix_4g.png';
            case 5:
                return 'matrix_mini.jpg';
            default:
                return 'matrix_mini.jpg';
        }
    }

    onCameraChange($event: any) {
        let vm = this;
        let camera_id = $event.source.value;

        function ChangeCameraType() {
            vm.SelectCamera(camera_id);
            vm.selectedTabId = vm.tabs[0].id;

            let cameraId = camera_id;

            vm.queryCameraId = cameraId;
            vm.SelectCamera(vm.queryCameraId);
            vm.GetCameraSettings();
        }

        let props: GenericCameraSettings[] = [];
        this.InitialValues.forEach((prop) => {
            AddToProps(prop);
        });

        function AddToProps(prop: GenericCameraSettings) {
            if (prop.value != prop.defaultValue) {
                props.push(prop);
            }
        }

        if (props.length > 0) {
            Swal.fire({
                title: 'You are about to change camera type, all unsaved settings will be lost',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#0f1c24',
                confirmButtonText: 'Yes, I am sure!',
                cancelButtonText: 'No, cancel it!',
                cancelButtonColor: '#FFFFFF',
                showClass: {
                    popup: 'animated fadeInDown faster'
                },
                hideClass: {
                    popup: 'animated fadeOutUp faster'
                },
                reverseButtons: true
            }).then((result) => {
                props = [];
                if (result.isConfirmed) {
                    ChangeCameraType();
                } else {
                    this.selectedCamera = this.selectedCamera = cameraList.find(
                        (x: Camera) => x.id == this.selectedCameraIdOld
                    );

                    this.selectedCameraId = this.selectedCameraIdOld;
                    this.ChangeDetecter.detectChanges();
                }
            });
        } else {
            ChangeCameraType();
        }
    }

    compareCategoryObjects(object1: any, object2: any) {
        return object1 && object2 && object1.id == object2.id;
    }

    SelectCamera(id: number) {
        this.selectedCamera = cameraList.filter((x: Camera) => x.id == id)[0];
        this.selectedCameraId = this.selectedCamera.id;
        this.selectedCameraImage = this.getCameraImage(this.selectedCameraId);

        switch (id) {
            case 3:
                this.InitialValues = Matrix2PlusProps;
                this.FormProperties = Matrix2PlusProps;
                break;
            case 4:
                this.InitialValues = Matrix4GProps;
                this.FormProperties = Matrix4GProps;
                break;
            case 5:
                this.InitialValues = MatrixMiniProps;
                this.FormProperties = MatrixMiniProps;
                break;
            default:
                this.InitialValues = MatrixMiniProps;
                this.FormProperties = MatrixMiniProps;
                break;
        }

        this.SetupCameraSettings();
    }

    private InitializeForm() {
        this.form = GenericCameraSettingsFunctions.toFormGroup(
            this.FormProperties
        );
    }

    updateValues(ob: any) {
        this.FormProperties.forEach((x) => {
            if (x.propertyName == ob.name) {
                x.value = ob.value;
            }
        });

        this.ApplyEffects2(ob.name);
        this.ChangeDetecter.detectChanges();
    }

    onTabChange(tab: any) {
        this.scrollToService.scrollTo({
            target: tab.title + '_' + tab.id,
            duration: 500
        });

        this.selectedTabId = tab.id;
        this.searchText = '';

        this.filterInput();
    }

    onSubmit() {
        if (!this.form.valid) {
            let groupArr = [];
            let error =
                '<ul style="text-align:left;font-size: 15px;color: #F27474;">';

            Object.keys(this.form.controls).forEach((key) => {
                let control = this.form.get(key);

                control.markAsTouched();
                control.updateValueAndValidity();

                const controlErrors: ValidationErrors = control.errors;
                let prop = this.InitialValues.find(
                    (x) => x.propertyName == key
                );

                if (controlErrors != null) {
                    groupArr.push(prop.groupId);
                    Object.keys(controlErrors).forEach((keyError) => {
                        error += `<li>${prop.validationErrorMessages[keyError]}</li>`;
                        console.warn(
                            `${key}, ${keyError}${controlErrors[keyError]}`
                        );
                    });
                }
            });

            error += '</ul>';

            const distinctGroupArr = Array.from(new Set(groupArr));

            this.CameraSettings.forEach((x) => {
                x.groups.forEach((y) => {
                    if (distinctGroupArr.indexOf(y.group.id) >= 0) {
                        y.isOpen = true;
                    }
                });
            });

            this.ChangeDetecter.detectChanges();
            this.SweetAlert('error', 'Please fix the validation errors', error);

            return;
        }

        let modifiedProps: GenericCameraSettings[] = [];
        let props: GenericCameraSettings[] = [];

        this.InitialValues.forEach((prop) => {
            if (prop.value != prop.defaultValue) {
                modifiedProps.push(prop);
            }
            props.push(prop);
        });

        let grouping: CameraSettingTab[] = [];
        for (let i in this.tabs) {
            let groups = this.groups.filter((x) => x.tabId == this.tabs[i].id);
            let groupForCameraSetting: CameraSettingGroup[] = groups.map(
                (x: Group) => {
                    return {group: x, isOpen: true};
                }
            );

            groupForCameraSetting.forEach((x) => {
                x.initialValues = modifiedProps.filter(
                    (y) => y.groupId == x.group.id
                );
            });

            groupForCameraSetting = groupForCameraSetting.filter(
                (x) => x.initialValues.length > 0
            );

            if (groupForCameraSetting.length > 0) {
                grouping.push({
                    tab: this.tabs[i],
                    groups: groupForCameraSetting
                });
            }
        }

        let data = {
            properties: grouping,
            userId: this.queryUserId,
            userName: this.queryUsername,
            roleId: Number(this.queryRoleId),
            roleName: this.RoleName,
            companyId: Number(this.queryCompanyId),
            companyName: this.OrganizationName,
            organizations: this.Organizations,
            isSiteAdmin: this.isSiteAdmin,
            roles: this.Roles,
            mode: this.mode,
            cameraId: this.queryCameraId
        };

        const dialogRef = this.dialog.open(ChangesDialogComponent, {
            data: data,
            panelClass: 'custom-dialog-container'
        });

        dialogRef.componentInstance.UndoClick.subscribe((result) => {
            this.InitialValues.forEach((x) => {
                if (x.propertyName == result) {
                    x.value = x.defaultValue;
                }
            });
            this.FormProperties.forEach((x) => {
                if (x.propertyName == result) {
                    x.value = x.defaultValue;
                }
            });
            this.InitializeForm();
            props = props.filter((x) => x.propertyName != result);
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result && result.Status === true) {
                Swal.fire({
                    html: `
                            <h2 style="font-weight: 600;">Applying camera settings!</h2>
                            <p style="color: #777;">Please wait...</p>
                        `,
                    allowOutsideClick: false,
                    didOpen: () => Swal.showLoading()
                });

                let sCamera: CameraType = 5;
                switch (this.selectedCamera.id) {
                    case CameraTypes.Matrix2Plus:
                        sCamera = CameraType.Matrix2Plus;
                        break;
                    case CameraTypes.Matrix4G:
                        sCamera = CameraType.Matrix4G;
                        break;
                    case CameraTypes.MatrixMini:
                        sCamera = CameraType.MatrixMini;
                        break;
                }

                let p = props.map((x) => {
                    let value = x.value;

                    if (
                        x.inputType == InputType.Number &&
                        x.renderType == RenderType.Toggle
                    ) {
                        value = Number(value);
                    }

                    if (value === false || value === true) {
                        value = Number(value);
                    }

                    return {[x.propertyName]: value};
                });

                let o = {root: {}};
                p.forEach((x) => {
                    Object.assign(o.root, x);
                });

                let cameraSetting: ExternalCameraSetting = null;

                if (result.UpdateType === 'User') {
                    cameraSetting = {
                        updateMode: ApiMode.User,
                        cameraType: sCamera,
                        updateID: this.queryUserId,
                        updatePayload: JSON.stringify(o)
                    };
                } else if (result.UpdateType === 'Organization') {
                    cameraSetting = {
                        updateMode: ApiMode.Company,
                        cameraType: sCamera,
                        updateID: result.OrganizationId,
                        updatePayload: JSON.stringify(o)
                    };
                } else if (result.UpdateType === 'Role') {
                    cameraSetting = {
                        updateMode: ApiMode.Role,
                        cameraType: sCamera,
                        updateID: result.RoleId,
                        updatePayload: JSON.stringify(o)
                    };
                } else {
                    cameraSetting = {
                        updateMode: ApiMode.Company,
                        cameraType: sCamera,
                        updateID: 0,
                        updatePayload: JSON.stringify(o)
                    };
                }

                this.cameraSettingService
                    .updateCameraSettings(cameraSetting)
                    .subscribe({
                        next: (x) => {
                            Swal.close();
                            this.SweetAlert(
                                'success',
                                'Success',
                                'Settings saved successfully'
                            ).then((x) => {
                                this.form.reset();
                                this.GetCameraSettings();
                                this.FormInitialization();
                            });
                        },
                        error: (err) => {
                            Swal.close();
                            this.SweetAlert(
                                'error',
                                'Technical error',
                                'Unable to save camera settings'
                            );
                        }
                    });
            }
        });
    }

    filterInput() {
        this.FormProperties = this.InitialValues.filter((property) => {
            if (!this.searchText || this.PropertyNameCheck(property)) {
                return property;
            }
        });
    }

    PropertyNameCheck(property: GenericCameraSettings): boolean {
        let vm = this;
        let a =
            property.displayName
                .toLowerCase()
                .indexOf(vm.searchText.toLowerCase()) > -1;
        return a;
    }

    PropertyNameDisplayCheck(propertyName: string): boolean {
        let vm = this;
        let a =
            propertyName.toLowerCase().indexOf(vm.searchText.toLowerCase()) >
            -1;
        return a || !this.searchText;
    }

    PropertyNameDisplayCheckGroup(
        properties: GenericCameraSettings[]
    ): boolean {
        let vm = this;
        let a: boolean = false;
        properties.forEach((x) => {
            a =
                a ||
                x.displayName
                    .toLowerCase()
                    .indexOf(vm.searchText.toLowerCase()) > -1;
        });
        return a || !this.searchText;
    }

    updateSearchText($event: any) {
        $event.preventDefault();
        this.searchText = $event.target.value;
        this.filterInput();
    }

    private SweetAlert(icon: SweetAlertIcon, title: string, text: string) {
        return Swal.fire({
            title: title,
            html: text,
            icon: icon,
            confirmButtonColor: '#0f1c24',
            showClass: {
                popup: 'animated fadeInDown faster'
            },
            hideClass: {
                popup: 'animated fadeOutUp faster'
            }
        });
    }

    ngOnInit() {
        this.organizationsService
            .getOrganizations()
            .subscribe((res: Organization[]) => {
                this.Organizations = res;
                this.OrganizationName = res.find(
                    (x) => x.CompanyId == this.queryCompanyId
                )?.Name;
            });

        this.roleService.getRoles().subscribe((res: Role[]) => {
            this.Roles = res;
            this.RoleName = res.find((x) => x.ID == this.queryRoleId)?.RoleName;
        });
    }

    ngOnChanges() {
        this.filterInput();
    }

    ngAfterViewInit() {
        this.selectedTabId = 1;
        this.onScroll(null);
    }
}
